import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { environment } from '../environments/environment';
import { CheckoutRecord } from '../core/models/equipment.models';
import { EquipmentService } from '../core/services/equipment.service';
import { InventoryService } from '../core/services/inventory.service';
import { ExportService } from '../core/services/export.service';
import { SettingsService } from '../core/services/settings.service';
import { AuthService } from '../modules/auth/auth.service';
import { confirmAction } from '../core/utils/confirm.util';
import {
  defaultBranchFilter,
  isBranchFilterLocked,
  matchesBranchFilter,
} from '../core/utils/branch-scope.util';
import { currentTimestamp, formatDate } from '../core/utils/format.util';

@Component({
  selector: 'app-check-in',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './check-in.component.html',
  styleUrls: ['./check-in.component.css'],
})
export class CheckInComponent implements OnInit {
  private readonly equipment = inject(EquipmentService);
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);

  readonly timestamp = currentTimestamp;
  readonly formatDate = formatDate;
  readonly allCheckouts = this.equipment.allCheckouts;
  readonly allAssets = this.equipment.allAssets;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));

  conditionOptions = signal([
    'Good',
    'Excellent',
    'Fair',
    'Poor',
    'Damaged',
    'Missing Parts',
    'Lost',
  ]);

  // ============================================
  // STATE
  // ============================================
  readonly searchQuery = signal('');
  readonly statusFilter = signal('');
  readonly periodFilter = signal<'all' | 'short' | 'medium' | 'long'>('all');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');
  private readonly tableSection =
    viewChild<ElementRef<HTMLElement>>('checkinTable');

  // Modal states
  readonly showReturnModal = signal(false);
  readonly showEditModal = signal(false);
  readonly selectedCheckoutId = signal('');
  readonly editingId = signal<string | null>(null);
  readonly editCustomer = signal('');
  readonly editDueDate = signal('');

  // Return form fields
  readonly returnReceiver = signal('');
  readonly returnReceivedBy = signal('');
  readonly returnBranch = signal('');
  readonly returnLocation = signal('');
  readonly returnCondition = signal('');
  readonly returnNotes = signal('');
  readonly returnDate = signal('');
  readonly returnQuantity = signal(1);
  readonly unreturnedFate = signal<'' | 'still-out' | 'lost' | 'damaged'>('');

  readonly returnQuantityMax = computed(() => {
    const checkout = this.selectedCheckout();
    if (!checkout) {
      return 1;
    }
    const outstanding = checkout.quantityOutstanding ?? checkout.quantity ?? 1;
    return Math.max(0, outstanding);
  });

  readonly unreturnedQuantity = computed(() => {
    const max = this.returnQuantityMax();
    const returning = Math.max(0, Math.min(this.returnQuantity(), max));
    return Math.max(0, max - returning);
  });

  readonly showUnreturnedFate = computed(() => this.unreturnedQuantity() > 0);

  readonly unreturnedFateHint = computed(() => {
    const remaining = this.unreturnedQuantity();
    if (remaining <= 0) {
      return '';
    }
    const fate = this.unreturnedFate();
    if (!fate) {
      return `Account for the ${remaining} unit(s) not coming back (or leave them still out).`;
    }
    if (fate === 'still-out') {
      return `${remaining} stay outstanding on this checkout for a later return.`;
    }
    if (fate === 'lost') {
      return `${remaining} will be marked lost / missing (not restocked).`;
    }
    return `${remaining} will be sent to Damaged / Lost as written-off (not restocked).`;
  });

  readonly selectedCheckout = computed(
    () =>
      this.allCheckouts().find((row) => row.id === this.selectedCheckoutId()) ??
      null,
  );

  // Toast
  readonly showToast = signal(false);
  readonly toastMessage = signal('');

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  // ============================================
  // COMPUTED
  // ============================================
  readonly activeCheckouts = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    const period = this.periodFilter();
    const branch = this.branchFilter();
    const location = this.locationFilter();

    return this.allCheckouts()
      .filter(
        (checkout) =>
          checkout.status === 'active' || checkout.status === 'overdue',
      )
      .filter(
        (checkout) =>
          (checkout.quantityOutstanding ?? checkout.quantity ?? 0) > 0,
      )
      .filter((checkout) => {
        const daysOverdue = this.daysOverdue(checkout);
        const isOverdue = checkout.status === 'overdue' || daysOverdue > 0;

        if (status === 'active' && isOverdue) {
          return false;
        }
        if (
          status &&
          checkout.status !== status &&
          !(status === 'overdue' && isOverdue)
        ) {
          return false;
        }

        if (period !== 'all') {
          if (!isOverdue) {
            return false;
          }
          if (period === 'short' && (daysOverdue < 1 || daysOverdue > 3)) {
            return false;
          }
          if (period === 'medium' && (daysOverdue < 4 || daysOverdue > 7)) {
            return false;
          }
          if (period === 'long' && daysOverdue < 8) {
            return false;
          }
        }

        if (
          !matchesBranchFilter(
            branch,
            checkout.branch,
            checkout.branchId,
            this.branches(),
          )
        ) {
          return false;
        }
        if (location !== 'all' && checkout.location !== location) {
          return false;
        }
        if (
          search &&
          !checkout.equipment.toLowerCase().includes(search) &&
          !checkout.customer.toLowerCase().includes(search) &&
          !checkout.checkoutNumber.toLowerCase().includes(search) &&
          !(
            checkout.receiver &&
            checkout.receiver.toLowerCase().includes(search)
          ) &&
          !(
            checkout.branch && checkout.branch.toLowerCase().includes(search)
          ) &&
          !(
            checkout.location &&
            checkout.location.toLowerCase().includes(search)
          )
        ) {
          return false;
        }
        return true;
      });
  });

  readonly overdueStats = computed(() => {
    const overdue = this.allCheckouts().filter(
      (checkout) =>
        checkout.status === 'overdue' || this.daysOverdue(checkout) > 0,
    );
    const buckets = { short: 0, medium: 0, long: 0 };

    for (const checkout of overdue) {
      const days = this.daysOverdue(checkout);
      if (days <= 3) {
        buckets.short += 1;
      } else if (days <= 7) {
        buckets.medium += 1;
      } else {
        buckets.long += 1;
      }
    }

    return {
      total: overdue.length,
      ...buckets,
    };
  });

  readonly stats = computed(() => {
    const all = this.allCheckouts();
    const active = all.filter((c) => c.status === 'active');
    const overdue = all.filter((c) => c.status === 'overdue');
    const today = new Date().toISOString().slice(0, 10);
    const dueToday = active.filter((c) => c.dueDate === today);

    return {
      checkedOut: active.length + overdue.length,
      dueToday: dueToday.length,
      overdue: overdue.length,
    };
  });

  // ============================================
  // METHODS
  // ============================================
  ngOnInit(): void {
    if (!environment.useMockData) {
      forkJoin([
        this.inventory.loadItemMasterAndStock(),
        this.equipment.refreshCheckInFromApi(),
        this.equipment.refreshOverdueFromApi(),
      ]).subscribe({
        next: () => this.applyRouteQueryParams(),
      });
      return;
    }
    this.applyRouteQueryParams();
  }

  private applyRouteQueryParams(): void {
    const checkoutId = this.route.snapshot.queryParamMap.get('checkout');
    const search = this.route.snapshot.queryParamMap.get('q');
    const status = this.route.snapshot.queryParamMap.get('status');
    if (search) {
      this.searchQuery.set(search);
    }
    if (status === 'overdue') {
      this.statusFilter.set('overdue');
    }
    if (checkoutId) {
      const checkout = this.allCheckouts().find((row) => row.id === checkoutId);
      if (checkout) {
        this.openReturnModal(checkout);
      }
    }
  }

  findAssetIcon(equipmentName: string): string {
    const asset = this.allAssets().find((row) => row.name === equipmentName);
    return asset?.icon ?? 'bi-box-seam';
  }

  findAssetId(equipmentName: string): string {
    const asset = this.allAssets().find((row) => row.name === equipmentName);
    return asset?.assetId ?? '';
  }

  daysOverdue(checkout: CheckoutRecord): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(checkout.dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.max(
      0,
      Math.ceil((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)),
    );
  }

  isCheckoutOverdue(checkout: CheckoutRecord): boolean {
    return checkout.status === 'overdue' || this.daysOverdue(checkout) > 0;
  }

  overdueSeverity(days: number): {
    rowClass: string;
    badgeClass: string;
    label: string;
  } {
    if (days >= 7) {
      return {
        rowClass: 'overdue-critical',
        badgeClass: 'critical',
        label: 'Critical',
      };
    }
    if (days >= 4) {
      return { rowClass: 'overdue-high', badgeClass: 'high', label: 'High' };
    }
    if (days >= 2) {
      return {
        rowClass: 'overdue-medium',
        badgeClass: 'medium',
        label: 'Medium',
      };
    }
    return { rowClass: 'overdue-low', badgeClass: 'low', label: 'Low' };
  }

  daysLeft(checkout: CheckoutRecord): { label: string; className: string } {
    const overdueDays = this.daysOverdue(checkout);
    if (this.isCheckoutOverdue(checkout)) {
      return {
        label: `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`,
        className: 'overdue',
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(checkout.dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diff <= 0) {
      return { label: 'Overdue', className: 'overdue' };
    }
    if (diff === 1) {
      return { label: '1 day', className: 'good' };
    }
    return { label: `${diff} days`, className: 'good' };
  }

  checkoutStatusLabel(checkout: CheckoutRecord): string {
    if (this.isCheckoutOverdue(checkout)) {
      return this.overdueSeverity(this.daysOverdue(checkout)).label;
    }
    return 'Checked Out';
  }

  viewAllOverdue(): void {
    this.statusFilter.set('overdue');
    this.periodFilter.set('all');
    this.searchQuery.set('');
    this.tableSection()?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  sendNotification(): void {
    this.showToastMessage('Notifications are not available yet.', 'info');
  }

  sendAllNotifications(): void {
    this.showToastMessage('Notifications are not available yet.', 'info');
  }

  // ============================================
  // MODAL METHODS
  // ============================================
  openReturnModal(checkout?: CheckoutRecord): void {
    if (checkout) {
      this.selectedCheckoutId.set(checkout.id);
      this.returnReceiver.set(checkout.receiver || checkout.customer || '');
      this.returnBranch.set(checkout.branch || '');
      this.returnLocation.set(checkout.location || '');
      this.returnQuantity.set(
        Math.max(0, checkout.quantityOutstanding ?? checkout.quantity ?? 1),
      );
    } else {
      const firstCheckout = this.activeCheckouts()[0];
      this.selectedCheckoutId.set(firstCheckout?.id ?? '');
      this.returnReceiver.set('');
      this.returnBranch.set('');
      this.returnLocation.set('');
      this.returnQuantity.set(
        Math.max(
          0,
          firstCheckout?.quantityOutstanding ?? firstCheckout?.quantity ?? 1,
        ),
      );
    }
    this.returnReceivedBy.set(this.auth.currentUserName());
    this.returnCondition.set('');
    this.returnNotes.set('');
    this.unreturnedFate.set('');
    this.returnDate.set(new Date().toISOString().slice(0, 10));
    this.showReturnModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  onReturnCheckoutSelect(checkoutId: string): void {
    this.selectedCheckoutId.set(checkoutId);
    const checkout = this.allCheckouts().find((row) => row.id === checkoutId);
    if (!checkout) {
      return;
    }
    this.returnReceiver.set(checkout.receiver || checkout.customer || '');
    this.returnBranch.set(checkout.branch || '');
    this.returnLocation.set(checkout.location || '');
    this.unreturnedFate.set('');
    this.returnQuantity.set(
      Math.max(0, checkout.quantityOutstanding ?? checkout.quantity ?? 1),
    );
  }

  onReturnQuantityChange(value: string | number): void {
    const parsed = Math.floor(Number(value));
    const max = this.returnQuantityMax();
    if (!Number.isFinite(parsed) || parsed < 0) {
      this.returnQuantity.set(0);
      return;
    }
    this.returnQuantity.set(Math.min(parsed, max));
    if (Math.min(parsed, max) >= max) {
      this.unreturnedFate.set('');
    }
  }

  closeReturnModal(): void {
    this.showReturnModal.set(false);
    this.selectedCheckoutId.set('');
    this.unreturnedFate.set('');
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeReturnModal();
      this.closeEditModal();
    }
  }

  openEditModal(checkout: CheckoutRecord): void {
    this.editingId.set(checkout.id);
    this.editCustomer.set(checkout.customer);
    this.editDueDate.set(checkout.dueDate);
    this.showEditModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeEditModal(): void {
    this.showEditModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  saveCheckout(): void {
    const id = this.editingId();
    const customer = this.editCustomer().trim();
    const dueDate = this.editDueDate();

    if (!id || !customer || !dueDate) {
      this.showToastMessage('Customer and due date are required', 'error');
      return;
    }

    this.equipment
      .updateCheckoutViaApi(id, { customer, dueDate, status: 'active' })
      .subscribe((result) => {
        if (result.success) {
          this.closeEditModal();
          this.showToastMessage('Checkout updated successfully', 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to update checkout',
            'error',
          );
        }
      });
  }

  deleteCheckout(checkout: CheckoutRecord): void {
    if (
      !confirmAction(
        `Cancel checkout for "${checkout.equipment}"? This will release the equipment.`,
      )
    ) {
      return;
    }

    this.equipment.deleteCheckoutViaApi(checkout.id).subscribe((result) => {
      if (result.success) {
        this.showToastMessage(
          'Checkout cancelled and equipment released',
          'success',
        );
      } else {
        this.showToastMessage(
          result.message ?? 'Failed to cancel checkout',
          'error',
        );
      }
    });
  }

  confirmReturn(): void {
    const checkoutId = this.selectedCheckoutId();
    const receiver = this.returnReceiver().trim();
    const receivedBy = this.returnReceivedBy().trim();
    const branch = this.returnBranch();
    const location = this.returnLocation();
    let condition = this.returnCondition();
    const returnDate = this.returnDate();
    const notes = this.returnNotes().trim();
    const outstanding = this.returnQuantityMax();
    const qtyReturning = Math.min(
      Math.max(0, this.returnQuantity()),
      outstanding,
    );
    const unreturned = Math.max(0, outstanding - qtyReturning);
    const fate = this.unreturnedFate();

    const storeLocationID = Number(
      this.locations().find((row) => row.name === location)?.id ?? 0,
    );
    const branchID = Number(
      this.branches().find((row) => row.name === branch)?.id ?? 0,
    );

    if (!checkoutId) {
      this.showToastMessage('Please select an asset', 'error');
      return;
    }

    if (!receiver || !receivedBy || !branch || !location) {
      this.showToastMessage('Please fill in all required fields', 'error');
      return;
    }

    if (!storeLocationID || storeLocationID <= 0) {
      this.showToastMessage(
        'Select a valid store location to receive the stock',
        'error',
      );
      return;
    }

    if (qtyReturning > 0 && !condition) {
      this.showToastMessage(
        'Please select the condition of the returned items',
        'error',
      );
      return;
    }

    if (qtyReturning === 0 && unreturned === 0) {
      this.showToastMessage(
        'Nothing to return — outstanding quantity is already 0',
        'error',
      );
      return;
    }

    if (unreturned > 0 && !fate) {
      this.showToastMessage(
        `Please say what happened to the ${unreturned} unit(s) not being returned`,
        'error',
      );
      return;
    }

    if (qtyReturning === 0 && fate === 'still-out') {
      this.showToastMessage(
        'Enter a return quantity, or mark the outstanding units as lost or damaged',
        'error',
      );
      return;
    }

    // No physical return — condition applies to write-off disposition.
    if (qtyReturning === 0 && !condition) {
      condition = fate === 'damaged' ? 'Damaged' : 'Lost';
      this.returnCondition.set(condition);
    }

    const checkout = this.allCheckouts().find((c) => c.id === checkoutId);
    if (!checkout) {
      this.showToastMessage('Checkout record not found', 'error');
      return;
    }

    const outstandingNow =
      checkout.quantityOutstanding ?? checkout.quantity ?? 0;
    if (outstandingNow <= 0) {
      this.showToastMessage(
        'This item is already fully returned or written off. Refresh Check-In and pick another item.',
        'error',
      );
      this.equipment.refreshCheckInFromApi().subscribe();
      return;
    }

    const routing = this.equipment.mapReturnCondition(condition);
    // Returned units: condition Lost → all counted as missing; Damaged → damaged portion of returned.
    let quantityReturned = routing.damageType === 'lost' ? 0 : qtyReturning;
    let quantityDamaged = routing.damageType === 'damaged' ? qtyReturning : 0;
    let quantityMissing = routing.damageType === 'lost' ? qtyReturning : 0;

    // Unreturned units (API: missing closes them out; damaged-not-returned uses missing + notes).
    if (unreturned > 0 && (fate === 'lost' || fate === 'damaged')) {
      quantityMissing += unreturned;
    }

    const dispositionNotes: string[] = [`Received by ${receivedBy}`, notes];
    if (unreturned > 0 && fate === 'still-out') {
      dispositionNotes.push(`${unreturned} unit(s) still outstanding`);
    }
    if (unreturned > 0 && fate === 'lost') {
      dispositionNotes.push(
        `${unreturned} unit(s) not returned — marked lost / missing`,
      );
    }
    if (unreturned > 0 && fate === 'damaged') {
      dispositionNotes.push(
        `${unreturned} unit(s) not returned — written off as damaged (Damaged / Lost)`,
      );
    }

    const damageDescription =
      unreturned > 0 && fate === 'damaged'
        ? `${unreturned} unit(s) not returned — written off as damaged`
        : unreturned > 0 && fate === 'lost'
          ? `${unreturned} unit(s) not returned — marked lost / missing`
          : undefined;

    this.equipment
      .processReturnViaApi(checkoutId, {
        returnedByName: receiver,
        returnCondition: condition,
        returnNotes: dispositionNotes.filter(Boolean).join('. ') || undefined,
        returnDate,
        equipmentAssetID: checkout.equipmentAssetID,
        quantityReturned,
        quantityDamaged,
        quantityMissing,
        damageDescription,
        repairRequired: quantityDamaged > 0,
        storeLocationID,
        branchID: branchID > 0 ? branchID : undefined,
      })
      .subscribe((result) => {
        if (result.success) {
          this.closeReturnModal();
          const outQty = checkout.quantity ?? 1;
          const previouslyReturned = checkout.quantityReturned ?? 0;
          let message: string;
          if (qtyReturning === 0 && fate === 'lost') {
            message = `Marked ${unreturned} of ${outQty} as lost / missing`;
          } else if (qtyReturning === 0 && fate === 'damaged') {
            message = `Wrote off ${unreturned} of ${outQty} as damaged (Damaged / Lost)`;
          } else if (routing.damageType === 'lost') {
            message = `Marked ${qtyReturning} of ${outQty} as lost`;
          } else if (routing.damageType === 'damaged') {
            message = `Returned ${qtyReturning} damaged (of ${outQty} out) — sent to Damaged / Lost`;
          } else {
            message = `Returned ${qtyReturning} of ${outQty} to ${location} (was returned ${previouslyReturned} before)`;
          }
          if (unreturned > 0 && fate === 'still-out') {
            message += `. ${unreturned} still outstanding`;
          } else if (unreturned > 0 && fate === 'lost') {
            message += `. ${unreturned} marked lost / missing`;
          } else if (unreturned > 0 && fate === 'damaged') {
            message += `. ${unreturned} written off as damaged`;
          }
          this.showToastMessage(result.message ?? message, 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to process return',
            'error',
          );
        }
      });
  }

  reportDamaged(): void {
    this.returnCondition.set('Damaged');
    this.confirmReturn();
  }

  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  showSuccessToast(message: string): void {
    this.showToastMessage(message, 'success');
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    const rows = this.activeCheckouts().map((item) => ({
      'Checkout No.': item.checkoutNumber,
      Equipment: item.equipment,
      Customer: item.customer,
      Destination: item.destination || '—',
      'Qty Out': item.quantity ?? 1,
      Returned: item.quantityReturned ?? 0,
      Outstanding: item.quantityOutstanding ?? item.quantity ?? 1,
      Receiver: item.receiver || '—',
      Branch: item.branch || '—',
      Location: item.location || '—',
      'Checkout Date': item.checkoutDate,
      'Due Date': item.dueDate,
      Status: this.checkoutStatusLabel(item),
    }));

    if (rows.length === 0) {
      this.showToastMessage('No checkouts match the current filters.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `checkouts-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }
}
